import { startRender } from './render/app';
import { simulate, settleOffline } from './sim/simulate';
import { LocalStorageStore, loadGame, saveGame, SAVE_KEY } from './sim/storage';
import type { GameState, Dex, Island } from './sim/state';
import { MAX_ISLANDS } from './sim/state';
import {
  cellAt,
  cellIndex,
  cellLevel,
  totalGardenAttributeBreakdown,
  placeDeco,
  removeDeco,
  rotateDeco,
  pruneDeco,
  upgradeDeco,
  isEntranceCell,
  shapeForIslandIndex,
  type Orientation,
} from './sim/garden';
import { islandUnlockCost, unlockNextIsland, canUnlockNextIsland } from './sim/islands';
import { connectedPathCells } from './sim/path';
import { ENTRANCE_INDEX } from './sim/garden';
import { classifyGesture, isFastPinchIn } from './sim/swipe';
import {
  getDeco,
  ATTRIBUTE_KEYS,
  ATTRIBUTE_LABELS,
  upgradeCost,
  isUpgradableDeco,
  MAX_LEVEL,
} from './sim/decos';
import { getCustomer } from './sim/customers';
import {
  isPlantDeco,
  stageForGrowSec,
  STAGE_THRESHOLDS_SEC,
  type Planting,
} from './sim/plants';
import { developDrink, setSpecial, specialAttribute } from './sim/menu';
import { maxConcurrentFor } from './sim/reputation';
import { SPECIAL_LIGHT_COLORS, SPECIAL_LIGHT_DEFAULT } from './palette';
import { currentEnv, type EnvOverride, type EnvSnapshot } from './sim/env';
import { activeSynergies } from './sim/synergy';
import { vitalityAt, isWiltingDeco } from './sim/wilt';
import { TIME_OF_DAY_ORDER, type TimeOfDay } from './sim/daynight';
import { SEASON_ORDER, type Season } from './sim/season';
import { createBuildPanel } from './ui/build_panel';
import { createToastHost, visitToastMessage } from './ui/toast';
import { createDexModal } from './ui/dex_modal';
import { createMenuModal } from './ui/menu_modal';
import { createStatusModal } from './ui/status_modal';
import { createIslandModal } from './ui/island_modal';
import { createAwaySummaryPanel } from './ui/away_summary';
import { createPlantSheet, type PlantAction } from './ui/plant_sheet';
import { createAudioEngine } from './audio/audio';

const container = document.getElementById('app')!;
const chipsEl = document.getElementById('chips')!;
const paletteEl = document.getElementById('palette')!;
const helpEl = document.getElementById('help')!;
const helpBtn = document.getElementById('helpBtn')!;
const tooltipEl = document.getElementById('tooltip')!;
const statusBtn = document.getElementById('statusBtn')!;
const dexBtn = document.getElementById('dexBtn')!;
const muteBtn = document.getElementById('muteBtn')!;
const speedBtn = document.getElementById('speedBtn')!;
const resetBtn = document.getElementById('resetBtn')!;
const toastsEl = document.getElementById('toasts')!;
const dexModalEl = document.getElementById('dexModal')!;
const dexListEl = document.getElementById('dexList')!;
const statusModalEl = document.getElementById('statusModal')!;
const statusBodyEl = document.getElementById('statusBody')!;
const menuModalEl = document.getElementById('menuModal')!;
const menuLeadEl = document.getElementById('menuLead')!;
const menuListEl = document.getElementById('menuList')!;
const awayEl = document.getElementById('away')!;
const awayLeadEl = document.getElementById('awayLead')!;
const awayListEl = document.getElementById('awayList')!;
const plantSheetEl = document.getElementById('plantSheet')!;
const plantSheetTitleEl = document.getElementById('plantSheetTitle')!;
const plantSheetButtonsEl = document.getElementById('plantSheetButtons')!;
// 島ナビ（v0.7.1。v0.7.4 で ◀▶ ボタンは廃止＝フリック＋島選択画面＋ドットに集約）
const islandDotsEl = document.getElementById('islandDots')!;
const islandLabelEl = document.getElementById('islandLabel')!;
// 島の拡張パネル（v0.7.2: 島購入の格納先）
const islandBtn = document.getElementById('islandBtn') as HTMLButtonElement;
const islandModalEl = document.getElementById('islandModal')!;
const islandBodyEl = document.getElementById('islandBody')!;

/** 図鑑カウントの種別ごと合計を取り出す（来店検出の差分に使う）。 */
function dexCounts(dex: Dex): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, e] of Object.entries(dex)) out[id] = e.count;
  return out;
}

/** 成長段階の順位（成長キラッ音の検出に使う）。 */
const STAGE_RANK: Record<string, number> = { sprout: 0, sapling: 1, mature: 2, ancient: 3 };

/**
 * 前後の plantings を比べ、いずれかのセルで成長段階が 1 つ以上進んだかを返す。
 * before に無かったセル（新規植え付け）は段階上昇とは見なさない（設置音が別にある）。
 */
function plantStageAdvanced(
  before: Record<string, Planting>,
  after: Record<string, Planting>,
): boolean {
  for (const [idx, a] of Object.entries(after)) {
    const b = before[idx];
    if (!b) continue;
    const fromRank = STAGE_RANK[stageForGrowSec(b.growSec)] ?? 0;
    const toRank = STAGE_RANK[stageForGrowSec(a.growSec)] ?? 0;
    if (toRank > fromRank) return true;
  }
  return false;
}

/** デモ用: ホーム島（index 0）の特定セルの植物 growSec を手で設定する（ショーケース用）。 */
function setHomeGrowSec(state: GameState, idx: number, grow: number): GameState {
  const home = state.islands[0];
  const planting = home.plantings[String(idx)];
  if (!planting) return state;
  const islands = state.islands.slice();
  islands[0] = {
    garden: home.garden,
    plantings: { ...home.plantings, [String(idx)]: { ...planting, growSec: grow } },
  };
  return { ...state, islands };
}

/** デモ用: ?demo クエリで初期庭にデコを数個自動配置する（screenshot 用）。 */
function applyDemoGarden(state: GameState): GameState {
  let s: GameState = { ...state, chips: 600 };
  // 2 つ目の島（拡張島）をアンロックしてドットインジケータ（●○）が出る状態にする。
  // 費用ぶんを補ってからアンロックし、拡張島にも飾りを置く（ショーケース）。
  s = { ...s, chips: s.chips + islandUnlockCost(1) };
  s = unlockNextIsland(s);
  // 12×12（中央 (4,5)×(4,5) はカフェ被りで配置禁止、角は無効セル）。新グリッドの
  // 広さと円なりの端が映えるよう、有効セルに広く散らす（screenshot 用ショーケース）。
  const demo: [number, number, string][] = [
    // シナジーが映える隣接配置（v0.6.0）:
    [8, 4, 'crystal_cluster'],
    [9, 4, 'lantern'], // ランタン×クリスタル → 光+1（きらめく灯り）
    [9, 5, 'bench'], // ランタン×ベンチ → 静寂+1（灯りの読書席）
    [8, 7, 'fountain'],
    [3, 4, 'violet_bed'],
    [3, 3, 'glow_mushroom'], // 花壇×キノコ → 香り+1（しおれていなければ）
    [4, 8, 'crystal_cluster'],
    [3, 8, 'crystal_cluster'], // クリスタル同士 → 結晶+1（共鳴）
    [7, 8, 'wind_chime'],
    [2, 6, 'pond'],
    // 席をもう 1 つ（v0.7.0）。テーブル席を飾りの近くに置いて「席で飲む」を見せる。
    [7, 4, 'cafe_table'],
    // 季節の結晶（設置中はその季節に固定）。冬の結晶でショーケース。
    [2, 4, 'crystal_winter'],
  ];
  for (const [gx, gy, id] of demo) s = placeDeco(s, gx, gy, id);

  // 石畳パス（v0.7.0/v0.7.2）: 入口(9,9) → カフェ(占有ブロック {4,5}×{4,5} の縁) を石畳で
  // 「連続して」つなぐ。v0.7.2 では光る歩道（リボン）がショーケースで映えるよう、入口から
  // カフェ前まで途切れず連結させる（連結時のみリボン＋足あとガイドが出る）。
  // 入口左隣(8,9)から左へ→(6,8)で内側へ折れ→(6,6)→(5,6)=カフェ南の縁、で連結成立。
  const pathCells: [number, number][] = [
    [8, 9], [7, 9], [6, 9],
    [6, 8], [6, 7], [6, 6],
    [5, 6], // カフェ南の縁（占有ブロック (5,5) の真下＝カフェ隣接で連結成立）
    [9, 7], // 噴水(8,7)×石畳 → にぎやか+1（広場のシナジーを維持）
  ];
  for (const [gx, gy] of pathCells) s = placeDeco(s, gx, gy, 'stone_tile');

  // タイルのアップグレード（v0.7.0）: 飾りを育ててレベルピップ（⬆）と発光・大きさの差を見せる。
  // クリスタルを Lv3、ランタンを Lv2 まで上げる（チップは潤沢に補給）。
  // v0.7.2: 拡張パネルの「広げる」ボタンが有効状態で映るよう、次の島(💰6000)が買える額にする。
  s = { ...s, chips: s.chips + 9000 };
  s = upgradeDeco(s, 8, 4); // crystal_cluster → Lv2
  s = upgradeDeco(s, 8, 4); // → Lv3
  s = upgradeDeco(s, 9, 4); // lantern → Lv2

  // M4 ショーケース: 4 段階の木を一列に並べて成長差がひと目で分かるようにする。
  // それぞれ別セルに植え、plantings の growSec を段階の閾値に手で合わせる。
  const trees: [number, number, number][] = [
    [3, 6, 0], // 芽
    [4, 6, STAGE_THRESHOLDS_SEC.sapling + 1], // 若木
    [7, 6, STAGE_THRESHOLDS_SEC.mature + 1], // 成木
    [8, 8, STAGE_THRESHOLDS_SEC.ancient + 1], // 古木
  ];
  for (const [gx, gy, grow] of trees) {
    s = placeDeco(s, gx, gy, 'sapling');
    const idx = cellIndex(gx, gy);
    s = setHomeGrowSec(s, idx, grow);
  }
  // 満開の花壇（古木相当）も 1 つ。
  s = placeDeco(s, 2, 5, 'violet_bed');
  s = setHomeGrowSec(s, cellIndex(2, 5), STAGE_THRESHOLDS_SEC.ancient + 1);

  // 拡張島（index 1 = square）にも飾りを置く（「2島目を表示」ショットで映える庭にする）。
  // 拡張島はカフェ被りの制約が無いので中央近くにも置ける。正方の四隅・辺まで使って形を見せる。
  const island1: [number, number, string][] = [
    [3, 3, 'crystal_cluster'], // 正方の左上寄り
    [8, 3, 'lantern'], // 右上寄り
    [3, 8, 'pond'], // 左下寄り
    [8, 8, 'fountain'], // 右下寄り
    [5, 5, 'violet_bed'],
    [6, 6, 'glow_mushroom'],
    [4, 7, 'bench'],
    [7, 4, 'crystal_summer'],
  ];
  for (const [gx, gy, id] of island1) s = placeDeco(s, gx, gy, id, 0, 1);

  // v0.7.4 ショーケース: さらに twin(2) と clover(3) もアンロックして、カルーセルに
  // 形の違う島が複数見切れる絵を撮れるようにする。各島の形なりに飾りを散らす。
  s = { ...s, chips: s.chips + islandUnlockCost(2) + islandUnlockCost(3) + 4000 };
  s = unlockNextIsland(s); // 島2 = twin（丸2つ＋橋）
  s = unlockNextIsland(s); // 島3 = clover
  // 島2（twin）: 左右の円それぞれに飾りを置き、間の橋（中央 z≈0 の細い帯）にも置いて
  //「2 つの島が橋でつながっている」が分かるようにする。
  const island2: [number, number, string][] = [
    [1, 6, 'crystal_cluster'], // 左の円
    [2, 4, 'lantern'], // 左の円（上寄り）
    [10, 6, 'fountain'], // 右の円
    [9, 7, 'violet_bed'], // 右の円（下寄り）
    [6, 6, 'pond'], // 橋の上（中央 z 帯）
    [5, 6, 'wind_chime'], // 橋の上
  ];
  for (const [gx, gy, id] of island2) s = placeDeco(s, gx, gy, id, 0, 2);
  // 島3（clover）: 4 つ葉の角の張り出しに置いて凹凸を見せる。
  const island3: [number, number, string][] = [
    [1, 1, 'crystal_cluster'], // 左上の葉（round では欠ける角）
    [10, 1, 'lantern'], // 右上の葉
    [1, 10, 'pond'], // 左下の葉
    [10, 10, 'fountain'], // 右下の葉
    [6, 6, 'violet_bed'], // 中央コア
  ];
  for (const [gx, gy, id] of island3) s = placeDeco(s, gx, gy, id, 0, 3);

  // M5 ショーケース: ドリンクをいくつか開発し、1 つを本日のスペシャルに設定
  // （メニューモーダルを開いたときに開発済み/未開発が両方見えるように）。
  s = developDrink(s, 'comet_latte');
  s = developDrink(s, 'crystal_ade');
  s = developDrink(s, 'mossy_smoothie');
  s = setSpecial(s, 'crystal_ade');
  // 評判を盛って同時滞在の上限を中盤相当（~5 体）にし、行列が映るようにする。
  s = { ...s, totalVisits: 120 };
  return s;
}

const store = new LocalStorageStore();

async function main(): Promise<void> {
  const params = new URLSearchParams(location.search);
  const isDemo = params.has('demo');

  // ?demo&tod=night / &season=autumn 等で昼夜帯・季節を固定（スクショ再現性のため）。
  // ?demo で帯指定が無いときは夜に固定する（夜は発光が映え、スクショのショーケースに向く）。
  const envOverride: EnvOverride = {};
  const todParam = params.get('tod');
  if (todParam && (TIME_OF_DAY_ORDER as readonly string[]).includes(todParam)) {
    envOverride.tod = todParam as TimeOfDay;
  } else if (isDemo) {
    envOverride.tod = 'night';
  }
  const seasonParam = params.get('season');
  if (seasonParam && (SEASON_ORDER as readonly string[]).includes(seasonParam)) {
    envOverride.season = seasonParam as Season;
  }

  // ロード → オフライン精算
  const loaded = await loadGame(store, Date.now());
  const settled = settleOffline(loaded, Date.now());
  let state: GameState = settled.state;
  if (isDemo) state = applyDemoGarden(state);

  // 表示中の島（v0.7.1）。?island=N で初期表示島を切り替える（screenshot 用）。
  // 範囲はアンロック済みにクランプ。
  let view = 0;
  const islandParam = Number(params.get('island'));
  if (Number.isInteger(islandParam) && islandParam >= 0) {
    view = Math.min(islandParam, state.unlockedIslands - 1);
  }

  /** 表示中の島のデータ。 */
  function viewIsland(): Island {
    return state.islands[view] ?? state.islands[0];
  }
  /** 表示中の島の庭グリッド。 */
  function viewGarden(): Island['garden'] {
    return viewIsland().garden;
  }
  /** 表示中の島の植物。 */
  function viewPlantings(): Island['plantings'] {
    return viewIsland().plantings;
  }
  /** 表示中がホーム島（カフェ・客・入口があるのはここだけ）か。 */
  function isHomeView(): boolean {
    return view === 0;
  }

  const render = startRender(container);

  // --- 現在環境（昼夜・季節）---
  // 実時刻（または ?demo の固定値）から環境を導き、描画に反映する。env は状態が変わったり
  // 時刻が進むと変わるので、定期的（後段の onFrame で間引いて）に更新する。
  function computeEnv(): EnvSnapshot {
    return currentEnv(state, Date.now(), envOverride);
  }
  let env = computeEnv();
  /** 環境（昼夜・季節）を描画へ適用し、しおれ・シナジーも更新する。 */
  function applyEnv(): void {
    render.diorama.setTimeOfDay(env.tod);
    render.diorama.setSeason(env.season);
    refreshGardenEffects();
  }
  /** ゆらぎ（しおれ）とシナジー連結線を現在時刻で再評価し描画へ反映する（表示中の島）。 */
  function refreshGardenEffects(): void {
    const nowMs = Date.now();
    const grid = viewGarden();
    render.garden.setVitality((idx: number) => {
      const c = grid[idx];
      if (!c || !isWiltingDeco(c.decoId)) return 1.0;
      return vitalityAt(c.decoId, idx, state.seed, nowMs);
    });
    render.garden.setSynergies(
      activeSynergies({
        garden: grid,
        plantings: viewPlantings(),
        wilted: (i: number) => {
          const c = grid[i];
          if (!c || !isWiltingDeco(c.decoId)) return false;
          return vitalityAt(c.decoId, i, state.seed, nowMs) < 0.5;
        },
      }),
    );
    // 光る歩道（導線リボン＋足あとガイド）。入口→カフェの連結石畳をリボン化する。
    // 入口があるのはホーム島だけなので、拡張島では歩道を消す（空配列）。
    render.garden.setPathRoute(isHomeView() ? connectedPathCells(grid, ENTRANCE_INDEX) : []);
  }

  // 庭デコの初期反映 + 客ビューに庭を渡す（お気に入りデコの参照に使う）
  render.garden.sync(viewGarden(), viewPlantings(), 0);
  render.customers.setGarden(viewGarden());
  // 評判から同時滞在の上限を初期化（行列の発生判断に使う）。
  render.customers.setMaxConcurrent(maxConcurrentFor(state));

  // === 島ナビ（v0.7.1: 隣の島＋スワイプ移動）===
  //
  // 表示中の島（view）のデータを garden_view / customer_view に sync し直す方式。
  // 拡張島ではカフェ・客・入口が無いので、カフェメッシュを隠し客ビューをクリアする。
  /** 表示中の島の庭・しおれ・シナジー・客ビューをまとめて同期する。 */
  function syncView(tSec: number): void {
    // 表示島の形状（v0.7.3）を描画へ反映: 小惑星上面の輪郭とセル枠を形に合わせる。
    const shape = shapeForIslandIndex(view);
    render.diorama.setShape(shape);
    render.garden.setShape(shape, isHomeView());
    render.garden.sync(viewGarden(), viewPlantings(), tSec);
    render.diorama.setCafeVisible(isHomeView()); // 拡張島はカフェ無し
    if (isHomeView()) {
      render.customers.setGarden(viewGarden());
      render.islandVisitors.clear(); // ホームは本格動線の customer_view が担当（ambient なし）
    } else {
      // 拡張島は本店の動線客は来ないが、季節の常連が ambient で通う（v0.8.5）。
      render.customers.clear();
      render.customers.setGarden(viewGarden());
      const fill = viewGarden().reduce((n, c) => n + (c ? 1 : 0), 0);
      render.islandVisitors.setIsland(view, fill);
    }
    refreshGardenEffects();
  }

  /**
   * カルーセル（隣島の見切れディスク）とカメラの引き/寄りを現在状態に合わせる（v0.7.2）。
   *
   * 複数島がある（or ゴースト島を見せられる）ときは引き画角にして隣島が見切れるようにし、
   * ホーム島しか無いときは寄り画角（導線クローズアップ）にする。
   */
  function refreshCarousel(): void {
    const showGhost = state.unlockedIslands < MAX_ISLANDS;
    const gardens = state.islands
      .slice(0, state.unlockedIslands)
      .map((is) => is.garden);
    render.carousel.setIslands(gardens, view, showGhost);
    // 隣島（or ゴースト）があるなら引き画角で見切れを見せる。単独島なら寄り（導線重視）。
    const hasNeighbor = state.unlockedIslands > 1 || showGhost;
    render.diorama.setWideView(hasNeighbor);
  }

  /** 島インジケータ（ドット）・◀▶・アンロックボタン・島名を現在状態で更新する。 */
  function refreshIslandNav(): void {
    // ドット: アンロック済みは実体、次の 1 つ（未アンロック）はゴースト表示。
    islandDotsEl.replaceChildren();
    const showGhost = state.unlockedIslands < MAX_ISLANDS;
    const dotCount = state.unlockedIslands + (showGhost ? 1 : 0);
    for (let i = 0; i < dotCount; i++) {
      const dot = document.createElement('span');
      dot.className = 'dot';
      const isGhost = i >= state.unlockedIslands;
      if (isGhost) {
        dot.classList.add('ghost');
      } else {
        if (i === view) dot.classList.add('active');
        dot.addEventListener('click', () => gotoIsland(i));
      }
      islandDotsEl.appendChild(dot);
    }
    // 島名。
    islandLabelEl.textContent = isHomeView() ? 'ホーム島' : `島 ${view + 1}`;
    // 「🪐 島」ボタン: 次の島がアンロック可能なときだけ控えめに光らせて気づかせる（購入はパネル内）。
    islandBtn.classList.toggle('canUnlock', canUnlockNextIsland(state));
  }

  /**
   * 表示島を切り替える（範囲クランプ + 同期 + ナビ更新 + カルーセル回転）。
   *
   * v0.7.2: 横スライドをやめ、カルーセルを「グイン」と回す（quick ease + わずかな
   * オーバーシュート）。回転は presentation 側（carousel.spin）で進み、回り切ったら
   * フォーカス島が手前中央に来る。フォーカス島本体も一瞬スケールを弾ませて手応えを出す。
   */
  function gotoIsland(next: number): void {
    const clamped = Math.max(0, Math.min(state.unlockedIslands - 1, next));
    if (clamped === view) return;
    const dir: 1 | -1 = clamped > view ? 1 : -1;
    view = clamped;
    audio.playSe('tap');
    // カルーセルを回す（新フォーカスを手前へ）。次に setIslands で確定配置に合わせる。
    render.carousel.spin(dir);
    syncView(currentTSec);
    refreshCarousel();
    refreshIslandNav();
    // フォーカス島の「グイン」着地: 一瞬縮めて弾むスケールバウンス（islandBounce が戻す）。
    islandBounceT = currentTSec;
  }
  /** 島切替時のフォーカス島スケールバウンスの起点（負で未発火）。 */
  let islandBounceT = -1;

  // 初期環境を適用（空・地面・しおれ・シナジー線）。
  applyEnv();
  syncView(0);
  refreshCarousel();
  refreshIslandNav();

  // デモ（スクショ）は自転を止め、入口(9,9)＝カメラ手前の角が正面に来る角度（0）で固定する。
  // これで「入口 → 光る歩道 → カフェ → 席で飲む客」の導線が毎回同じ向きで撮れる。
  if (isDemo) render.diorama.setSpinning(false);

  // スクショ用: ?panel=island で島の拡張パネルを開いた状態にする（モーダルの確認用）。
  if (params.get('panel') === 'island') {
    setTimeout(() => islandModal.open(state, view), 0);
  }

  // キーボード ←→ で島送り（PC）。◀▶ ボタンは v0.7.4 で廃止（フリック＋島選択画面に集約）。
  window.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') gotoIsland(view - 1);
    else if (e.key === 'ArrowRight') gotoIsland(view + 1);
  });

  // --- サウンド（M5 後半）---
  // 加速モードの倍率（ランタイムのみ。1 = 等速）。フレームループで simulate の dt に掛ける。
  let speed = 1;
  // 1 フレームで進めるゲーム内秒の上限（背景化からの復帰で巨大 dt が来てもカクつかせない安全弁）。
  const MAX_SIM_DT_PER_FRAME = 60;
  // リセット中フラグ。true の間はオートセーブ・離脱時保存をスキップ（消したセーブを保存し直さない）。
  let resetting = false;
  // 来店トーストの実時間スロットル。加速時に札が点滅し続けないよう、一定間隔に間引く。
  let lastVisitToastMs = 0;
  const VISIT_TOAST_COOLDOWN_MS = 700;

  // セーブのミュート設定で初期化。AudioContext はモバイルの自動再生制限のため
  // 初回ポインタ操作（unlock）まで作らない。
  const audio = createAudioEngine(state.muted);

  function refreshMuteBtn(): void {
    muteBtn.textContent = state.muted ? '♪ 音 OFF' : '♪ 音 ON';
    muteBtn.classList.toggle('muted', state.muted);
  }
  refreshMuteBtn();

  muteBtn.addEventListener('click', () => {
    audio.unlock(); // 念のため（最初の操作がミュートボタンの場合に備える）
    const nextMuted = !state.muted;
    audio.setMuted(nextMuted);
    state = { ...state, muted: nextMuted };
    refreshMuteBtn();
    void saveGame(store, { ...state, lastTickMs: Date.now() });
    if (!nextMuted) audio.playSe('tap'); // 解除時はタップ音でフィードバック
  });

  // --- 加速モード（処理速度だけ N 倍。ゲーム性は不変）---
  // ×1 / ×5 / ×20 を循環。ランタイムのみの設定（セーブしない＝再読込で ×1 に戻る）。
  const SPEED_CYCLE = [1, 5, 20];
  function refreshSpeedBtn(): void {
    speedBtn.textContent = `⏩ ×${speed}`;
    speedBtn.classList.toggle('on', speed !== 1);
  }
  refreshSpeedBtn();
  speedBtn.addEventListener('click', () => {
    const i = SPEED_CYCLE.indexOf(speed);
    speed = SPEED_CYCLE[(i + 1) % SPEED_CYCLE.length];
    refreshSpeedBtn();
    audio.unlock();
    audio.playSe('tap');
  });

  // --- リセット（セーブ消去 → 最初から）。誤爆防止に 2 度押し（armed 状態）---
  let resetArmed = false;
  let resetTimer = 0;
  resetBtn.addEventListener('click', () => {
    if (!resetArmed) {
      // 1 度目: armed にして「もう一度で消去」を促す。3 秒で自動解除。
      resetArmed = true;
      resetBtn.textContent = '⚠ もう一度で消去';
      resetBtn.classList.add('armed');
      audio.unlock();
      audio.playSe('tap');
      window.clearTimeout(resetTimer);
      resetTimer = window.setTimeout(() => {
        resetArmed = false;
        resetBtn.textContent = '🔄 リセット';
        resetBtn.classList.remove('armed');
      }, 3000);
      return;
    }
    // 2 度目: セーブを消してまっさらに再読込。
    // resetting=true でオートセーブ・離脱時保存を止める（消去を保存し直して打ち消さないため）。
    resetting = true;
    window.clearTimeout(resetTimer);
    resetBtn.textContent = 'リセット中…';
    void (async () => {
      await store.remove(SAVE_KEY);
      localStorage.removeItem('cometcafe.helpSeen'); // あそびかたガイドも初回扱いに戻す
      location.reload();
    })();
  });

  // 初回のユーザー操作で AudioContext を resume（自動再生制限の解除）。
  // pointerdown はキャンバス操作・ボタン操作のどちらでも先に発火する。
  window.addEventListener(
    'pointerdown',
    () => audio.unlock(),
    { once: true, capture: true },
  );

  // --- M3 UI: トースト / 図鑑 / るすの間サマリ ---
  const toasts = createToastHost(toastsEl);
  const dexModal = createDexModal({ modalEl: dexModalEl, listEl: dexListEl });
  const statusModal = createStatusModal({ modalEl: statusModalEl, bodyEl: statusBodyEl });
  const awayPanel = createAwaySummaryPanel({ rootEl: awayEl, leadEl: awayLeadEl, listEl: awayListEl });

  dexBtn.addEventListener('click', () => {
    audio.playSe('tap');
    if (dexModal.isOpen) dexModal.close();
    else dexModal.open(state.dex);
  });

  // ステータス画面（庭 📊）: 現在の環境・属性内訳・評判・シナジーをまとめて表示。
  statusBtn.addEventListener('click', () => {
    audio.playSe('tap');
    if (statusModal.isOpen) {
      statusModal.close();
      return;
    }
    env = computeEnv(); // 開く瞬間の最新環境で
    const nowMs = Date.now();
    // 属性は全島合算で表示する（来店レートに効くのは全島合算なので、ステータスもそれに揃える）。
    const grid = viewGarden();
    statusModal.open({
      state,
      breakdown: totalGardenAttributeBreakdown(state, nowMs),
      synergies: activeSynergies({
        garden: grid,
        plantings: viewPlantings(),
        wilted: (i: number) => {
          const c = grid[i];
          if (!c || !isWiltingDeco(c.decoId)) return false;
          return vitalityAt(c.decoId, i, state.seed, nowMs) < 0.5;
        },
      }),
      season: env.season,
      tod: env.tod,
    });
  });

  // カフェメニュー（カフェ本体タップで開く）。開発・スペシャル選択は純関数に委ね、
  // 反映後にモーダルを開き直して即時再描画する（開いたままなら再 render と同義）。
  const menuModal = createMenuModal({
    modalEl: menuModalEl,
    leadEl: menuLeadEl,
    listEl: menuListEl,
    onDevelop: (drinkId) => {
      const before = state;
      applyState(developDrink(state, drinkId));
      // 開発成功はチップ獲得音とは別の「設置」音で達成感を出す（拒否時は無音）。
      audio.playSe(state !== before ? 'place' : 'tap');
      menuModal.open(state.menu, state.chips);
    },
    onSelectSpecial: (drinkId) => {
      applyState(setSpecial(state, drinkId));
      audio.playSe('tap');
      menuModal.open(state.menu, state.chips);
    },
  });

  /**
   * 次の島をアンロックする（v0.7.2）。成功時は軽い達成演出（キラキラ＋効果音＋トースト
   * 「新しい島が広がった！」）に留める。盛大な購入演出は将来拡張（GAME_DESIGN.md の TODO）。
   * 新しい島へ移動して見せ、モーダルは開いたまま再描画する（連続購入の導線）。
   */
  function tryUnlockIsland(): void {
    const before = state;
    applyState(unlockNextIsland(state));
    if (state !== before) {
      // 軽い達成演出。
      audio.playSe('place');
      audio.playSe('grow'); // キラッ音
      toasts.show('✦ 新しい島が広がった！');
      gotoIsland(state.unlockedIslands - 1); // 開いた島へ移動して見せる
      islandModal.open(state, view); // パネルを最新状態で開き直す
    } else {
      audio.playSe('tap'); // 不足・上限など拒否
    }
    refreshIslandNav();
  }

  // 島の拡張パネル（🪐 島ボタンで開く）。一覧の島タップで移動、購入はここでだけ行う。
  const islandModal = createIslandModal({
    modalEl: islandModalEl,
    bodyEl: islandBodyEl,
    onGoto: (i) => gotoIsland(i),
    onUnlock: () => tryUnlockIsland(),
  });
  islandBtn.addEventListener('click', () => {
    audio.playSe('tap');
    if (islandModal.isOpen) islandModal.close();
    else islandModal.open(state, view);
  });

  // 建設 UI（パレット）。属性表示はステータス画面へ集約したので attrsEl は渡さない（v0.6.0）。
  const panel = createBuildPanel({
    paletteEl,
    onSelect: (decoId) => {
      // デコ選択中は建設モード: 自転を止めてセルを狙いやすくする。
      render.diorama.setSpinning(decoId == null);
      if (decoId == null) hover = null;
      refreshHint();
    },
  });
  panel.updateAffordability(state.chips);

  // --- ポインタ操作（タッチ/マウス統一） ---
  let hover: { gx: number; gy: number } | null = null;
  let ghostOrientation: Orientation = 0;
  // 描画の累計秒（onFrame で更新）。操作起点の演出（成長キラッ）の時刻基準に使う。
  let currentTSec = 0;

  // 植物アクションシート（盆栽タップ → 剪定 / 撤去）。
  const plantSheet = createPlantSheet({
    rootEl: plantSheetEl,
    titleEl: plantSheetTitleEl,
    buttonsEl: plantSheetButtonsEl,
    onAction: (gx, gy, action: PlantAction) => {
      if (action.type === 'remove') {
        const before = state;
        applyState(removeDeco(state, gx, gy, view));
        if (state !== before) audio.playSe('remove');
      } else {
        const before = state;
        applyState(pruneDeco(state, gx, gy, action.style, view));
        if (state !== before) audio.playSe('prune');
      }
    },
  });

  function ghostValidAt(gx: number, gy: number, decoId: string): boolean {
    if (cellAt(viewGarden(), gx, gy) != null) return false; // 既に埋まっている
    const deco = getDeco(decoId);
    return !!deco && state.chips >= deco.cost; // チップ足りる
  }

  function refreshHint(): void {
    const sel = panel.selectedId;
    render.garden.setHint({
      highlight: hover,
      ghostDecoId: sel,
      ghostCell: sel ? hover : null,
      ghostOrientation,
      ghostValid: sel && hover ? ghostValidAt(hover.gx, hover.gy, sel) : true,
    });
  }

  /**
   * 「本日のスペシャル」の属性色でカフェの光を更新する。未設定なら既定の暖色。
   * メニュー操作・初期化のたびに呼ぶ（属性→色は palette の SPECIAL_LIGHT_COLORS）。
   */
  function refreshSpecialLight(): void {
    const sp = specialAttribute(state.menu);
    const hex = sp ? SPECIAL_LIGHT_COLORS[sp.key] ?? SPECIAL_LIGHT_DEFAULT : null;
    render.diorama.setSpecialColor(hex);
  }

  function applyState(next: GameState): void {
    if (next === state) return; // 拒否された操作（同一参照）は無視
    state = next;
    render.garden.sync(viewGarden(), viewPlantings(), currentTSec);
    render.customers.setGarden(viewGarden()); // お気に入りデコ参照を更新
    panel.updateAffordability(state.chips);
    chipsEl.textContent = `チップ 💰 ${Math.floor(state.chips)}`;
    refreshSpecialLight(); // スペシャル変更を光に反映
    // 配置変更でシナジー連結線・しおれ・季節固定（結晶の着脱）が変わりうるので再評価。
    env = computeEnv();
    applyEnv();
    refreshCarousel(); // 島数・アンロック可否・ゴースト島の有無が変わりうる
    refreshIslandNav(); // チップ変動でアンロックの可否が変わりうる
    void saveGame(store, { ...state, lastTickMs: Date.now() }); // 操作のたびに保存
  }

  /**
   * 来店検出: simulate 前後の図鑑カウント差分から「今このフレームで来た客」を
   * 割り出し、描画スポーン + トーストを出す。図鑑カウントは丸められないので
   * 取りこぼし無く検出できる。tSec は描画の累計秒（スポーン演出の基準）。
   */
  function reactToVisits(before: Record<string, number>, after: Dex, tSec: number): void {
    let anyVisit = false;
    // トーストは実時間で間引く（加速モードで毎フレーム札が出て埋め尽くさないように）。
    // 来店自体（経済・スポーン）は間引かない。札の通知だけ一定間隔にする。
    const nowMs = Date.now();
    const showToast = nowMs - lastVisitToastMs >= VISIT_TOAST_COOLDOWN_MS;
    for (const [id, e] of Object.entries(after)) {
      const delta = e.count - (before[id] ?? 0);
      if (delta <= 0) continue;
      const def = getCustomer(id);
      if (!def) continue;
      // 描画スポーンは 1 種につき最大 3 体まで（加速時の大量同時来店で描画が溢れないように。
      // 残りは経済だけ成立＝customer_view 側の行列上限と整合）。
      for (let k = 0; k < Math.min(delta, 3); k++) render.customers.spawn(id, tSec);
      // トーストは 1 種につき 1 枚（多重来店はまとめる）。スロットル中はスキップ。
      if (showToast) {
        const tipTotal = def.tip * delta;
        toasts.show(visitToastMessage(def.name, def.icon, tipTotal));
      }
      anyVisit = true;
    }
    if (showToast && anyVisit) lastVisitToastMs = nowMs;
    if (anyVisit) {
      // 来店チャイム + チップ獲得音（このフレームで来た客があれば 1 度ずつ）。
      audio.playSe('arrive');
      audio.playSe('tip');
    }
  }

  const canvas = render.canvas;

  // === ポインタ操作の状態機械（v0.7.3 / v0.7.4 ジェスチャ調停）===
  //
  // ■ 1 本指:
  //   - 単タップ（移動なし・長押し未満）= 設置 / 選択ツールチップ / カフェメニュー / 剪定シート
  //   - ドラッグ（移動閾値 DRAG_PX 超え）= **常に島内回転**（横移動を Y 回転にライブ反映）。
  //     ゆっくり眺め回す限り何度横に動かしても回転だけ（絶対にページ送りしない）。
  //   - フリック（指を離す瞬間の水平速度が速い横の弾き）= **そのとき初めて 1 島だけ送る**。
  //   - 長押し（HOLD_MS 以上・移動なし）= 非植物デコの撤去（震え＋❌＋ﾆｭﾆｭﾝ音＋全額返金）
  // ■ 2 本指（v0.7.4）:
  //   - ピンチ = **カメラズーム**（2 指間距離の比でフォーカス島に寄る/引く）。ピンチ中は
  //     1 本指の回転・フリック・長押し・タップを抑止する（誤爆させない）。
  //   - **素早いピンチイン（一気にすぼめる）** = 指を離す瞬間に島選択画面を開く（isFastPinchIn）。
  //     ゆっくりピンチは 3 のズームに留め、画面遷移しない。
  // ■ デスクトップ: ホイール=回転を維持（ズームは触らない）。
  //
  // v0.7.2 までは「ドラッグ開始時に横優位なら page モードに固定」していたため、島を回したい
  // だけの横ドラッグが即・島送りに倒れて敏感すぎた。v0.7.3 ではドラッグ中は常に回転にし、
  // 島送りは pointerup での速度判定（classifyGesture）に一本化する。
  //
  // タッチ/マウス両対応。回転と撤去・設置が誤爆しないよう移動閾値で整合させる。
  const DRAG_PX = 8; // これを超えて動いたらドラッグ扱い（タップ/長押しをキャンセル）
  const HOLD_MS = 600; // 長押し撤去の成立時間
  // フリックの水平速度を測るための直近サンプル保持窓（ms）。離す瞬間の vx を出すのに使う。
  const FLICK_SAMPLE_MS = 90;

  // --- ピンチ（2 本指）の状態（v0.7.4）---
  // アクティブなポインタ座標（pointerId → 画面座標）。2 本になったらピンチに入る。
  const activePointers = new Map<number, { x: number; y: number }>();
  interface PinchState {
    startDist: number; // ピンチ開始時の 2 指間距離
    startMs: number; // ピンチ開始時刻
    zoomAtStart: number; // 開始時のカメラズーム
    lastDist: number; // 直近の 2 指間距離
    minDist: number; // 観測した最小距離（一気にすぼめたか＝島選択の判定に使う）
    minMs: number; // 最小距離になった時刻
    openedSelector: boolean; // 既に島選択を開いたか（多重発火防止）
  }
  let pinch: PinchState | null = null;

  /** 2 点間のユークリッド距離。 */
  function dist2(a: { x: number; y: number }, b: { x: number; y: number }): number {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  interface PointerSession {
    id: number;
    startX: number;
    startY: number;
    lastX: number;
    startCell: { gx: number; gy: number } | null;
    onDeco: boolean; // 押した位置に非植物デコがある（長押し撤去の候補）
    dragging: boolean; // 移動閾値を超えてドラッグ（＝島内回転）に入った
    holdTimer: number | null; // 長押し成立タイマー
    holdFired: boolean; // 長押しが成立して撤去済み
    holdStartT: number; // 長押し開始の累計秒（❌ 進捗の演出基準）
    // フリック判定用の直近サンプル（x 座標・時刻 ms）。pointerup で末尾窓から vx を出す。
    samples: { x: number; y: number; t: number }[];
  }
  let ps: PointerSession | null = null;

  /** 長押し撤去の進捗演出を毎フレーム更新するための候補（null で解除）。 */
  let holdCandidate: { gx: number; gy: number; startMs: number } | null = null;

  /**
   * デコ選択ツールチップを出す（単タップ＝確認のみ。撤去しない）。v0.7.0: デコがレベルを
   * 持つなら現在レベルと「⬆ アップグレード（コスト）」ボタンを足す（タップで段階強化）。
   */
  function showTooltip(gx: number, gy: number, clientX: number, clientY: number): void {
    const cell = cellAt(viewGarden(), gx, gy);
    if (!cell) return;
    const deco = getDeco(cell.decoId);
    if (!deco) return;
    const level = cellLevel(cell);
    const attrs = ATTRIBUTE_KEYS.filter((k) => (deco.attributes[k] ?? 0) > 0)
      .map((k) => `${ATTRIBUTE_LABELS[k]} ${deco.attributes[k]}`)
      .join('　');
    const upgradable = isUpgradableDeco(cell.decoId);
    tooltipEl.innerHTML =
      `<span class="nm">${deco.name}</span>` +
      `<span class="at">${attrs || '無属性'}</span>` +
      (upgradable ? `<span class="lv">Lv.${level} / ${MAX_LEVEL}</span>` : '') +
      `<span class="hold">長押しで撤去（全額もどる）</span>`;

    // アップグレードボタン（最大レベルは「最大」表示・無効化、チップ不足は無効化）。
    tooltipEl.classList.toggle('actionable', upgradable);
    if (upgradable) {
      const cost = upgradeCost(cell.decoId, level);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'upBtn';
      if (!Number.isFinite(cost)) {
        btn.classList.add('max');
        btn.textContent = '⬆ 最大レベル';
        btn.disabled = true;
      } else {
        btn.textContent = `⬆ アップグレード（💰${cost}）`;
        btn.disabled = state.chips < cost;
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const before = state;
          applyState(upgradeDeco(state, gx, gy, view));
          if (state !== before) {
            audio.playSe('place');
            // 反映後の新コスト・レベルで吹き出しを更新（同じ位置に出し直す）。
            showTooltip(gx, gy, clientX, clientY);
          } else {
            audio.playSe('tap');
          }
        });
      }
      tooltipEl.appendChild(btn);
    }

    tooltipEl.style.left = `${clientX}px`;
    tooltipEl.style.top = `${clientY}px`;
    tooltipEl.classList.add('show');
    window.clearTimeout(tooltipHideTimer);
    // アクション可能なツールチップは長めに出す（操作時間を確保）。
    tooltipHideTimer = window.setTimeout(
      () => {
        tooltipEl.classList.remove('show');
        tooltipEl.classList.remove('actionable');
      },
      upgradable ? 4200 : 1800,
    );
  }
  let tooltipHideTimer = 0;

  /** 長押し成立: 非植物デコをポンと撤去して全額返金（音・演出つき）。 */
  function fireRemoval(gx: number, gy: number): void {
    const before = state;
    applyState(removeDeco(state, gx, gy, view));
    if (state !== before) {
      audio.playRemovalWobble(); // ﾆｭﾆｭﾝ
      audio.playSe('remove');
    }
    holdCandidate = null;
    render.garden.setRemovalCandidate(null, 0);
  }

  canvas.addEventListener('pointermove', (e) => {
    // ピンチ（2 本指）中: ズーム＋素早いピンチイン検出（v0.7.4）。1 本指処理より優先。
    if (pinch && activePointers.has(e.pointerId)) {
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (activePointers.size >= 2) {
        const pts = [...activePointers.values()];
        const d = dist2(pts[0], pts[1]);
        pinch.lastDist = d;
        // ズーム = 開始ズーム × (現在距離 / 開始距離)。指を広げる→寄る、すぼめる→引く。
        if (pinch.startDist > 1) {
          render.diorama.setZoom(pinch.zoomAtStart * (d / pinch.startDist));
        }
        // 最小距離（一気にすぼめた度合い）を追う。
        if (d < pinch.minDist) {
          pinch.minDist = d;
          pinch.minMs = performance.now();
        }
        // 素早いピンチイン（開始距離→最小距離を短時間で）なら島選択画面を開く（1 回だけ）。
        if (
          !pinch.openedSelector &&
          isFastPinchIn(pinch.startDist, pinch.minDist, pinch.minMs - pinch.startMs)
        ) {
          pinch.openedSelector = true;
          audio.unlock();
          audio.playSe('tap');
          islandModal.open(state, view); // 島選択画面（拡張パネルを兼ねる）
        }
      }
      return;
    }

    // ホバー更新（設置ゴースト用）。ドラッグ中はゴーストより回転を優先。
    if (!ps || !ps.dragging) {
      hover = render.picker.pick(e.clientX, e.clientY);
      refreshHint();
    }
    if (!ps || ps.id !== e.pointerId) return;

    const dx = e.clientX - ps.startX;
    const dy = e.clientY - ps.startY;
    // フリック速度推定用に直近サンプルを記録（古い窓は pointerup 側で切る）。
    ps.samples.push({ x: e.clientX, y: e.clientY, t: performance.now() });
    if (ps.samples.length > 12) ps.samples.shift();

    if (!ps.dragging && Math.hypot(dx, dy) > DRAG_PX) {
      // 移動閾値を超えた → ドラッグ開始。タップ・長押しをキャンセル。
      ps.dragging = true;
      if (ps.holdTimer != null) {
        window.clearTimeout(ps.holdTimer);
        ps.holdTimer = null;
      }
      holdCandidate = null;
      render.garden.setRemovalCandidate(null, 0);
    }
    if (ps.dragging) {
      // ドラッグは常に島内回転。横移動を島の Y 回転に写す（右へドラッグ→時計回り）。
      // 島送りはここでは絶対に行わない（pointerup の速度判定 classifyGesture に一本化）。
      const moveX = e.clientX - ps.lastX;
      ps.lastX = e.clientX;
      const rect = canvas.getBoundingClientRect();
      const rad = (moveX / Math.max(1, rect.width)) * Math.PI * 2.2;
      render.diorama.nudgeRotation(rad);
    }
  });

  canvas.addEventListener('pointerleave', () => {
    hover = null;
    refreshHint();
  });

  canvas.addEventListener('pointerdown', (e) => {
    // ピンチ調停（v0.7.4）: アクティブポインタを記録。2 本目が来たらピンチに入り、
    // 1 本指セッション（回転/長押し/タップ）をキャンセルする（誤爆させない）。
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointers.size === 2) {
      // 1 本指セッションの進行中演出を畳む。
      if (ps && ps.holdTimer != null) window.clearTimeout(ps.holdTimer);
      ps = null;
      holdCandidate = null;
      render.garden.setRemovalCandidate(null, 0);
      const pts = [...activePointers.values()];
      const d = dist2(pts[0], pts[1]);
      pinch = {
        startDist: d,
        startMs: performance.now(),
        zoomAtStart: render.diorama.getZoom(),
        lastDist: d,
        minDist: d,
        minMs: performance.now(),
        openedSelector: false,
      };
      return;
    }
    if (activePointers.size > 2) return; // 3 本目以降は無視（ピンチ中）

    const cell = render.picker.pick(e.clientX, e.clientY);
    const existing = cell ? cellAt(viewGarden(), cell.gx, cell.gy) : null;
    // 入口ゲートはホーム島の固定セル（撤去不可）。拡張島には入口が無いので制約しない。
    const isEntrance = !!cell && isHomeView() && isEntranceCell(cell.gx, cell.gy);

    ps = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      startCell: cell,
      // 入口ゲートは撤去不可なので長押し撤去の対象にしない（v0.7.0）。
      onDeco: !!existing && !isPlantDeco(existing.decoId) && !isEntrance,
      dragging: false,
      holdTimer: null,
      holdFired: false,
      holdStartT: currentTSec,
      samples: [{ x: e.clientX, y: e.clientY, t: performance.now() }],
    };

    // 非植物デコ（入口を除く）の上で押し始めたら、長押し撤去のカウントダウンを開始（震え＋❌）。
    if (cell && existing && !isPlantDeco(existing.decoId) && !isEntrance) {
      holdCandidate = { gx: cell.gx, gy: cell.gy, startMs: performance.now() };
      render.garden.setRemovalCandidate({ gx: cell.gx, gy: cell.gy }, 0);
      ps.holdTimer = window.setTimeout(() => {
        if (ps && !ps.dragging) {
          ps.holdFired = true;
          fireRemoval(cell.gx, cell.gy);
        }
      }, HOLD_MS);
    }
  });

  function endPointer(e: PointerEvent): void {
    // ピンチ（2 本指）の後始末（v0.7.4）。指が 1 本に減ったらピンチ終了。残った 1 本は
    // そのまま 1 本指セッションに引き継がない（ピンチの抜けで回転/タップが誤爆しないように）。
    if (pinch) {
      activePointers.delete(e.pointerId);
      if (activePointers.size < 2) {
        pinch = null;
        ps = null; // 念のため（ピンチ中に作られていない想定だが安全側）
      }
      return;
    }
    activePointers.delete(e.pointerId);

    if (!ps || ps.id !== e.pointerId) return;
    const session = ps;
    ps = null;
    // 長押しタイマーが残っていれば止める。
    if (session.holdTimer != null) window.clearTimeout(session.holdTimer);
    // ドラッグ中だった/長押しで撤去済みなら、ここでの「タップ」処理は行わない。
    if (session.dragging || session.holdFired) {
      // ドラッグの「離す瞬間」が素早い横フリックなら島送り（pointerup 速度判定に一本化）。
      // ゆっくり眺め回したドラッグは vx が小さく rotate のまま＝ページしない（敏感さの解消）。
      if (session.dragging && !session.holdFired) {
        const now = performance.now();
        // 末尾 FLICK_SAMPLE_MS 窓の最古サンプルを基準に、離す瞬間の dx/dy/dt を出す。
        const samples = session.samples;
        const last = samples[samples.length - 1];
        let ref = samples[0];
        for (let i = samples.length - 1; i >= 0; i--) {
          ref = samples[i];
          if (now - samples[i].t >= FLICK_SAMPLE_MS) break;
        }
        const fdx = last.x - ref.x;
        const fdy = last.y - ref.y;
        const fdt = last.t - ref.t;
        const g = classifyGesture(fdx, fdy, fdt);
        if (g.kind === 'page') gotoIsland(view + g.direction);
      }
      holdCandidate = null;
      render.garden.setRemovalCandidate(null, 0);
      refreshHint();
      return;
    }
    // 長押し未成立で指を離した → 撤去はキャンセルし、ここから先は単タップとして扱う。
    holdCandidate = null;
    render.garden.setRemovalCandidate(null, 0);

    // --- 単タップの処理 ---
    // カフェ本体タップ → メニュー（デコ未選択時のみ。ホーム島だけ＝拡café はホーム島のみ）。
    if (isHomeView() && !panel.selectedId && render.picker.pickCafe(e.clientX, e.clientY)) {
      audio.playSe('tap');
      menuModal.open(state.menu, state.chips);
      hideHint();
      return;
    }

    const cell = session.startCell;
    if (!cell) return;
    hover = cell;
    const existing = cellAt(viewGarden(), cell.gx, cell.gy);

    if (existing) {
      if (isPlantDeco(existing.decoId)) {
        // 植物: 従来どおり剪定シート（撤去ボタンも残す）。
        const deco = getDeco(existing.decoId);
        const planting = viewPlantings()[String(cellIndex(cell.gx, cell.gy))];
        const stage = stageForGrowSec(planting ? planting.growSec : 0);
        plantSheet.open({ gx: cell.gx, gy: cell.gy, name: deco ? deco.name : '植物', stage });
        hideHint();
      } else {
        // 非植物デコの単タップ＝選択/確認のみ（撤去はしない）。ツールチップ＋ハイライト。
        // v0.7.0: ツールチップにアップグレードアクションを足す（cell 座標を渡す）。
        audio.playSe('tap');
        showTooltip(cell.gx, cell.gy, e.clientX, e.clientY);
        render.garden.setHint({ highlight: cell });
        hideHint();
      }
    } else if (panel.selectedId) {
      // 空セル + デコ選択中: 設置（表示中の島へ）
      const before = state;
      applyState(placeDeco(state, cell.gx, cell.gy, panel.selectedId, ghostOrientation, view));
      if (state !== before) audio.playSe('place'); // 拒否（チップ不足等）なら鳴らさない
      hideHint();
    }
    refreshHint();
  }

  canvas.addEventListener('pointerup', endPointer);
  canvas.addEventListener('pointercancel', endPointer);

  // マウスホイールで島を手動回転（PC）。縦ホイールを回転量に写す。
  // Ctrl+ホイールはカメラズーム（v0.7.4。デスクトップ任意。ピンチ相当の拡縮）。
  canvas.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      if (e.ctrlKey) {
        // 上スクロール（deltaY<0）で寄る、下で引く。1 ノッチ ~8% 拡縮。
        const factor = e.deltaY < 0 ? 1.08 : 1 / 1.08;
        render.diorama.setZoom(render.diorama.getZoom() * factor);
      } else {
        render.diorama.nudgeRotation((e.deltaY > 0 ? 1 : -1) * 0.18);
      }
    },
    { passive: false },
  );

  // 右クリック（PC）で置いてあるデコを 90° 回転（無料）。
  // タッチではメニューを出さず、撤去→再設置で向きを選ぶ運用（シンプル優先）。
  canvas.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    const cell = render.picker.pick(e.clientX, e.clientY);
    if (cell && cellAt(viewGarden(), cell.gx, cell.gy)) {
      applyState(rotateDeco(state, cell.gx, cell.gy, view));
    }
  });

  // タッチ操作の妨げになるブラウザ既定のジェスチャを抑える（キャンバス上のみ）。
  canvas.style.touchAction = 'none';

  function hideHint(): void {
    helpEl.classList.remove('open');
  }

  // あそびかたパネル: ？ボタンで開閉。初回起動時だけ自動で開く（以後は出さない）。
  const HELP_SEEN_KEY = 'cometcafe.helpSeen';
  helpBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    helpEl.classList.toggle('open');
    localStorage.setItem(HELP_SEEN_KEY, '1');
  });
  helpEl.addEventListener('click', hideHint);
  if (!isDemo && !localStorage.getItem(HELP_SEEN_KEY)) {
    helpEl.classList.add('open');
    localStorage.setItem(HELP_SEEN_KEY, '1');
  }

  // フレーム時刻に基づき毎フレーム sim を進める（描画とロジックは分離）。
  let lastTSec = 0;
  // 環境（昼夜・季節）とゆらぎの再評価は毎フレームでは重い・不要なので数秒おきに間引く。
  let lastEnvCheckT = 0;
  render.onFrame = (tSec) => {
    currentTSec = tSec;
    const dt = tSec - lastTSec;
    lastTSec = tSec;

    // 環境チェック（約 4 秒おき）。帯・季節が変わったら描画へ反映。しおれは連続変化
    // なので毎回 refreshGardenEffects で活力を更新する（しおれ⇔復活の遷移を見せる）。
    if (tSec - lastEnvCheckT > 4) {
      lastEnvCheckT = tSec;
      const nextEnv = computeEnv();
      if (nextEnv.tod !== env.tod || nextEnv.season !== env.season) {
        env = nextEnv;
        render.diorama.setTimeOfDay(env.tod);
        render.diorama.setSeason(env.season);
      }
      refreshGardenEffects(); // しおれの進行を反映
    }

    // 長押し撤去の進捗（経過 / HOLD_MS）を毎フレーム描画へ反映（震え・❌ の大きさ）。
    if (holdCandidate) {
      const elapsed = performance.now() - holdCandidate.startMs;
      const progress = Math.min(1, elapsed / HOLD_MS);
      render.garden.setRemovalCandidate({ gx: holdCandidate.gx, gy: holdCandidate.gy }, progress);
    }
    // 島切替の「グイン」着地: フォーカス島を一瞬縮めて弾ませる（カルーセルの回転と同調）。
    // SPIN_DUR=0.44 と尺を揃え、縮み→オーバーシュートを強めて「回り込んで着地した」手応えを出す。
    if (islandBounceT >= 0) {
      const e = (tSec - islandBounceT) / 0.44; // carousel の SPIN_DUR と揃える
      if (e >= 1) {
        render.diorama.island.scale.setScalar(1);
        islandBounceT = -1;
      } else {
        // 0 で 0.86 倍（ぐっと縮む）→ 中盤で弾んで 1.07 までオーバーシュート → 1.0 に収束。
        const s = 0.86 + 0.21 * Math.sin(e * Math.PI) + 0.14 * e;
        render.diorama.island.scale.setScalar(Math.min(1.07, s));
      }
    }

    const beforeChips = Math.floor(state.chips);
    const beforeCounts = dexCounts(state.dex);
    // 成長キラッ・段階イベントは表示中の島で意味を持つので、表示島の植物を控える。
    const beforeViewPlantings = viewPlantings();
    const beforeTotalVisits = state.totalVisits;
    // 加速モード: ゲーム内時間だけ speed 倍で進める（ゲーム性＝来店ロジックは不変、処理が速いだけ）。
    // 1 フレームで進める量は上限でクランプ（背景化からの復帰で巨大 dt が来てもカクつかせない）。
    const simDt = Math.min(dt * speed, MAX_SIM_DT_PER_FRAME);
    state = simulate(state, simDt);
    // 来店があれば描画スポーン + トースト（客が来るのはホーム島だけ。拡張島表示中は出さない）。
    if (isHomeView()) reactToVisits(beforeCounts, state.dex, tSec);
    // 累計来店が増えたら評判が動くので同時滞在の上限を更新（行列の閾値が育つ）。
    if (state.totalVisits !== beforeTotalVisits) {
      render.customers.setMaxConcurrent(maxConcurrentFor(state));
    }
    // 植物が成長したら庭ビューを差分同期（段階が上がればメッシュ更新＋キラッ演出）。
    // 表示中の島の plantings 参照が変われば growSec が進んでいるので sync を呼ぶ。
    const afterViewPlantings = viewPlantings();
    if (afterViewPlantings !== beforeViewPlantings) {
      render.garden.sync(viewGarden(), afterViewPlantings, tSec);
      // 成長段階が上がった植物があれば「成長キラッ」音を鳴らす（1 フレーム 1 度）。
      if (plantStageAdvanced(beforeViewPlantings, afterViewPlantings)) audio.playSe('grow');
    }
    const after = Math.floor(state.chips);
    if (after !== beforeChips) {
      chipsEl.textContent = `チップ 💰 ${after}`;
      panel.updateAffordability(state.chips);
    }
  };

  // 初期表示
  chipsEl.textContent = `チップ 💰 ${Math.floor(state.chips)}`;
  refreshSpecialLight(); // 起動時にスペシャルの光を反映（?demo はスペシャル設定済み）
  refreshHint();

  // るすの間サマリ（オフライン精算で来店があったときだけ出す）
  awayPanel.show(settled.summary);

  // デモ（?demo）: screenshot に客が映るよう、見本の客を即時スポーンする
  // （ショーケースなので出現条件は問わず、5 種をひととおり並べる）。客はホーム島のみ。
  if (isDemo && isHomeView()) {
    // v0.7.2: 導線（席で飲む・歩く・並ぶ）の各状態が映るよう、十分前にスポーンして
    // 先頭の数体は席に着き「飲んでいる」状態に、後続は歩行/行列で見えるようにする。
    // 入口ゲートが大きな客で隠れないよう、入口前にたまる行列ぶんは控えめにする。
    const seatedHeadStart = [
      ['yumemi', 12], // 静寂席に着いて飲んでいる
      ['nemu', 10], // 着席して飲んでいる
      ['prism', 8], // 着席
    ] as const;
    for (const [id, ago] of seatedHeadStart) render.customers.spawn(id, -ago);
    // 歩行中（トレイル付き）に映る客（少し前にスポーン＝まだカフェ/席へ歩いている途中）。
    render.customers.spawn('mokomoke', -1.2);
    render.customers.spawn('lunamoth', -0.6);
    // 入口前の行列を 2 体だけ（ゲートは見えるていど）。
    render.customers.spawn('paddle', 0);
    render.customers.spawn('mokomoke', 0.1);
    // 来店トーストの見本も 1 枚（レイアウト確認用）。
    const luna = getCustomer('lunamoth');
    if (luna) toasts.show(visitToastMessage(luna.name, luna.icon, luna.tip));
  }

  // 定期オートセーブ（lastTickMs を現在時刻に更新して保存）。リセット中は保存しない。
  setInterval(() => {
    if (resetting) return;
    void saveGame(store, { ...state, lastTickMs: Date.now() });
  }, 5000);

  // 離脱時にも保存（リセットによる再読込のときは消去を打ち消さないようスキップ）。
  window.addEventListener('beforeunload', () => {
    if (resetting) return;
    void saveGame(store, { ...state, lastTickMs: Date.now() });
  });
}

void main();
